import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as DOCUMENT_EXTRACT_QUERY_KEY } from './useDocumentExtract';
import { QUERY_KEY as DOCUMENT_SUMMARY_QUERY_KEY } from './useGetDocumentSummary';

interface ReprocessResponse {
  success: boolean;
  message: string;
  document_id: string;
}

const METHOD = 'POST';
const ENDPOINT = 'document/:id/reprocess';
export const QUERY_KEY = (id: string) => getQueryKey(METHOD, ENDPOINT, { id });

/**
 * Hook to reprocess a document with AI
 * @returns Mutation hook for reprocessing a document
 */
export const useReprocessDocument = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ReprocessResponse, Error, string>({
    mutationFn: async (documentId: string) => {
      const response = await handleApiCall<ReprocessResponse>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {
          id: documentId,
        })
      );

      return response;
    },
    onSuccess: (data, documentId) => {
      // Invalidate related queries to refetch the updated data
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_SUMMARY_QUERY_KEY(documentId),
      });
      queryClient.invalidateQueries({
        queryKey: DOCUMENT_EXTRACT_QUERY_KEY(documentId),
      });
    },
  });
};
